import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { ListScreen, Notice, TableSkeleton } from '../components/chrome/list-screen'
import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Icon } from '../components/icon'
import { Button, IconButton } from '../components/ui/button'
import { Dialog } from '../components/ui/dialog'
import { Field, Input, Select } from '../components/ui/field'
import { Blank, Panel, Row, Skeleton } from '../components/ui/panel'
import { Table, TBody, Td, Th, THead, Tr } from '../components/ui/table'
import { Chip, Dot, Legend, type Tone } from '../components/ui/tone'
import {
  ApiError,
  api,
  type AuthCheck,
  type AuthReport,
  type DnsCapability,
  type Domain,
  type DomainPurpose,
  type HealthState,
  type VerifyResult,
} from '../lib/api'
import { useSession } from '../lib/session'

/**
 * Domains — §13, §15, §18, §79.
 *
 * Two surfaces, one decision: the list is dense and scannable, the detail is where a domain
 * explains itself. That pairing is what lets the list show four authentication checks as four dots
 * — the sentences live one click away rather than being cut for space.
 *
 * §18 is what the whole screen is for. A domain does not send until its authentication actually
 * verifies in public DNS, and losing that authentication later is a degradation an operator has to
 * see. Verification is a gate, not a badge.
 */

/** The order §15 checks them in, and the order they are shown in everywhere. */
const CHECKS = ['SPF', 'DKIM', 'DMARC', 'MX'] as const

const HEALTH_TONE: Record<HealthState, Tone> = {
  HEALTHY: 'ok',
  WARNING: 'warn',
  BROKEN: 'bad',
  // Grey, never green. §49 applied to a 7px circle: "not checked" must not look like
  // "checked and fine".
  UNKNOWN: 'neutral',
}

const HEALTH_WORD: Record<HealthState, string> = {
  HEALTHY: 'verified',
  WARNING: 'missing or weak',
  BROKEN: 'broken',
  UNKNOWN: 'not checked',
}

const PURPOSES: Array<{ value: DomainPurpose; label: string; hint: string }> = [
  {
    value: 'ACQUISITION_OUTREACH',
    label: 'Outreach',
    hint: 'Cold email. This is what the product sends from.',
  },
  {
    value: 'UNSUBSCRIBE',
    label: 'Unsubscribe',
    hint: 'Serves the one-click unsubscribe endpoint (§31).',
  },
  { value: 'TRACKING', label: 'Tracking', hint: 'Link and open tracking, if enabled.' },
  {
    value: 'CORE_ORGANIZATIONAL',
    label: 'Core — organisation',
    hint: 'The main domain. §44 forbids sending outreach from it.',
  },
  {
    value: 'CORE_JOURNAL',
    label: 'Core — journal',
    hint: 'Editorial mail. §44 forbids sending outreach from it.',
  },
]

const PURPOSE_LABEL = Object.fromEntries(PURPOSES.map((p) => [p.value, p.label]))

function stateTone(state: string): Tone {
  if (state === 'ACTIVE') return 'ok'
  if (state === 'DEGRADED') return 'bad'
  if (state === 'READY') return 'accent'
  return 'neutral'
}

function humanise(value: string) {
  return value.toLowerCase().split('_').join(' ')
}

/** One check's stored state, matched case-insensitively — `checkType` is lowercase on the wire. */
function checkState(domain: Domain, name: string): HealthState {
  return (
    domain.checks.find((check) => check.checkType.toLowerCase() === name.toLowerCase())?.result ??
    'UNKNOWN'
  )
}

export function DomainsScreen({ onCrumb }: { onCrumb: (crumb: string | undefined) => void }) {
  const { can } = useSession()
  const [selected, setSelected] = useState<string | null>(null)

  const domains = useQuery({
    queryKey: ['domains'],
    queryFn: () => api.get<Domain[]>('/domains'),
    enabled: can('manage_domains'),
    refetchInterval: 60_000,
  })

  const current = domains.data?.find((d) => d.id === selected) ?? null

  useEffect(() => {
    onCrumb(current?.fqdn)
    return () => onCrumb(undefined)
  }, [current?.fqdn, onCrumb])

  if (!can('manage_domains')) {
    return (
      <Screen>
        <ScreenHeader title="Domains" />
        <Panel>
          <Blank
            icon="denied"
            title="You do not have access to domains"
            body="Viewing and configuring sending domains requires the manage_domains permission."
          />
        </Panel>
      </Screen>
    )
  }

  if (current) {
    return <DomainDetail domain={current} onBack={() => setSelected(null)} />
  }

  return <DomainList query={domains} onOpen={setSelected} />
}

// ── the list ──────────────────────────────────────────────────────────────────

function DomainList({
  query,
  onOpen,
}: {
  query: ReturnType<typeof useQuery<Domain[]>>
  onOpen: (id: string) => void
}) {
  const [adding, setAdding] = useState(false)
  const [filter, setFilter] = useState('')
  const domains = query.data ?? []
  const unhealthy = domains.filter((d) => d.health !== 'HEALTHY' && d.state !== 'RETIRED')
  const rows = domains.filter(
    (d) => !filter || d.fqdn.toLowerCase().includes(filter.toLowerCase()),
  )

  return (
    <>
      <ListScreen
        title="Domains"
        subtitle="Where outreach sends from. A domain does not send until its authentication verifies in public DNS (§18)."
        query={query.data ? filter : undefined}
        onQuery={setFilter}
        queryPlaceholder="Filter domains"
        savedViews={[]}
        primary={
          <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
            Add domain
          </Button>
        }
        notice={
          unhealthy.length > 0 ? (
            <Notice tone="warn">
              {unhealthy.length === 1
                ? 'One domain’s authentication is not verifying.'
                : `${unhealthy.length} domains’ authentication is not verifying.`}{' '}
              A domain in that state cannot be activated, and one that was already active has
              degraded since it was.
            </Notice>
          ) : undefined
        }
        hint="Four dots per row: SPF, DKIM, DMARC, MX. Grey means not checked — never that it passed."
      >
        {query.isLoading ? (
          <TableSkeleton columns={7} />
        ) : query.isError ? (
          <Blank
            icon="error"
            tone="bad"
            title="Domains could not be loaded"
            body={query.error instanceof ApiError ? query.error.message : undefined}
            action={<Button onClick={() => void query.refetch()}>Try again</Button>}
          />
        ) : rows.length === 0 ? (
          domains.length === 0 ? (
            <Blank
              icon="domains"
              title="No domains yet."
              body="Outreach never sends from the domains that carry editorial mail — add a separate one here (§44)."
              action={
                <Button variant="primary" onClick={() => setAdding(true)}>
                  Add a domain
                </Button>
              }
            />
          ) : (
            <Blank
              icon="noResults"
              title={`Nothing matches “${filter}”.`}
              action={<Button onClick={() => setFilter('')}>Clear filter</Button>}
            />
          )
        ) : (
          <>
            <Table>
              <THead>
                <Th>Domain</Th>
                <Th width={170}>Purpose</Th>
                <Th width={130}>State</Th>
                {CHECKS.map((check) => (
                  <Th key={check} width={64} align="center">
                    {check}
                  </Th>
                ))}
                <Th width={110} align="right">
                  Mailboxes
                </Th>
              </THead>
              <TBody>
                {rows.map((domain) => (
                  <Tr key={domain.id} onOpen={() => onOpen(domain.id)}>
                    <Td>
                      <span className="text-text font-mono text-[12px] font-medium">
                        {domain.fqdn}
                      </span>
                      {domain.isProtected && (
                        <Chip className="ml-2" tone="info">
                          protected
                        </Chip>
                      )}
                    </Td>
                    <Td className="text-muted">
                      {PURPOSE_LABEL[domain.purpose] ?? humanise(domain.purpose)}
                    </Td>
                    <Td>
                      <Chip tone={stateTone(domain.state)}>{humanise(domain.state)}</Chip>
                    </Td>
                    {CHECKS.map((check) => {
                      const state = checkState(domain, check)
                      return (
                        <Td key={check} align="center">
                          <Dot tone={HEALTH_TONE[state]} label={`${check}: ${HEALTH_WORD[state]}`} />
                        </Td>
                      )
                    })}
                    <Td align="right" className="text-muted">
                      {domain._count.mailboxes || '—'}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>

            {/* Four dots replace four sentences only because the legend is on the same screen. */}
            <div className="border-line bg-panel2 flex flex-wrap items-center gap-4 border-t px-[14px] py-2.5">
              <Legend tone="ok">verified in public DNS</Legend>
              <Legend tone="warn">missing or weak</Legend>
              <Legend tone="bad">changed unexpectedly</Legend>
              <Legend tone="neutral">not checked yet</Legend>
            </div>
          </>
        )}
      </ListScreen>

      <AddDomainDialog open={adding} onClose={() => setAdding(false)} onOpen={onOpen} />
    </>
  )
}

// ── the detail ────────────────────────────────────────────────────────────────

function DomainDetail({ domain, onBack }: { domain: Domain; onBack: () => void }) {
  const queryClient = useQueryClient()
  const [report, setReport] = useState<AuthReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  const capability = useQuery({
    queryKey: ['domain-capability', domain.id],
    queryFn: () => api.get<DnsCapability>(`/domains/${domain.id}/dns-capability`),
  })

  const verify = useMutation({
    mutationFn: () => api.post<VerifyResult>(`/domains/${domain.id}/verify`),
    onSuccess: async (result) => {
      setReport(result.verification)
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['domains'] })
    },
    onError: (e: Error) => setError(e.message),
  })

  const publish = useMutation({
    mutationFn: () =>
      api.post<{ verification: AuthReport }>(`/domains/${domain.id}/dns/publish`),
    onSuccess: async (result) => {
      setReport(result.verification)
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['domains'] })
    },
    onError: (e: Error) => setError(e.message),
  })

  const activate = useMutation({
    mutationFn: () => api.post<Domain>(`/domains/${domain.id}/activate`),
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['domains'] })
    },
    onError: (e: Error) => setError(e.message),
  })

  // Live checks when a verification has been run in this session; the stored rows otherwise.
  const checks: AuthCheck[] =
    report?.checks ??
    CHECKS.map((name) => {
      const stored = domain.checks.find((c) => c.checkType.toLowerCase() === name.toLowerCase())
      return {
        name,
        state: stored?.result ?? 'UNKNOWN',
        found: stored?.detail?.found ?? null,
        summary: stored?.message ?? 'Not checked yet.',
        fix: null,
        notes: stored?.detail?.notes ?? [],
      }
    })

  const failing = checks.filter((c) => c.state !== 'HEALTHY')
  const passing = checks.filter((c) => c.state === 'HEALTHY')
  const canPublish = capability.data?.automatic === true && failing.some((c) => c.fix)
  const healthy = domain.health === 'HEALTHY'

  return (
    <Screen>
      <button
        onClick={onBack}
        className="text-muted hover:text-text mb-3 inline-flex items-center gap-1.5 text-[12px] transition-colors"
      >
        <Icon name="chevronLeft" size={12} />
        Domains
      </button>

      <ScreenHeader
        title={<span className="font-mono">{domain.fqdn}</span>}
        meta={
          <>
            <Chip tone={stateTone(domain.state)}>{humanise(domain.state)}</Chip>
            {domain.isProtected && <Chip tone="info">protected</Chip>}
          </>
        }
        actions={
          <>
            <Button icon="refresh" onClick={() => verify.mutate()} disabled={verify.isPending}>
              {verify.isPending ? 'Checking DNS…' : 'Recheck DNS'}
            </Button>
            {domain.state !== 'ACTIVE' && (
              <Button
                variant="primary"
                onClick={() => activate.mutate()}
                disabled={activate.isPending || !healthy}
                title={
                  healthy
                    ? undefined
                    : 'Authentication must verify in public DNS before this domain can send (§18)'
                }
              >
                Activate for sending
              </Button>
            )}
          </>
        }
      />

      {error && (
        <div className="bg-badbg text-bad mb-4 rounded-[9px] px-4 py-3 text-[12.5px]">{error}</div>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Panel
            title={
              failing.length === 0
                ? 'Everything verifies'
                : `${failing.length} ${failing.length === 1 ? 'record needs' : 'records need'} attention`
            }
            description={
              report
                ? `Checked against public resolvers at ${new Date(report.checkedAt).toLocaleTimeString()} — public DNS, not your provider's dashboard.`
                : 'Run a check to see what public DNS actually returns for this domain.'
            }
            actions={
              canPublish && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => publish.mutate()}
                  disabled={publish.isPending}
                >
                  {publish.isPending ? 'Publishing…' : 'Publish records'}
                </Button>
              )
            }
          >
            {failing.map((check) => (
              <CheckBlock key={check.name} check={check} />
            ))}
            {passing.map((check) => (
              <PassingBlock key={check.name} check={check} />
            ))}
          </Panel>
        </div>

        <div className="space-y-4">
          <Panel title="This domain">
            <Row label="Purpose" labelWidth={130}>
              {PURPOSE_LABEL[domain.purpose] ?? humanise(domain.purpose)}
            </Row>
            <Row label="State" labelWidth={130}>
              {humanise(domain.state)}
            </Row>
            <Row label="Authentication" labelWidth={130}>
              <span className="inline-flex items-center gap-2">
                <Dot tone={HEALTH_TONE[domain.health]} label={HEALTH_WORD[domain.health]} />
                {HEALTH_WORD[domain.health]}
              </span>
            </Row>
            <Row label="Mailboxes" labelWidth={130}>
              {domain._count.mailboxes || 'none yet'}
            </Row>
            <Row label="Daily limit" labelWidth={130}>
              {domain.dailyLimit ?? 'not set'}
            </Row>
            <Row label="Added" labelWidth={130}>
              {new Date(domain.createdAt).toLocaleDateString()}
            </Row>
          </Panel>

          <Panel title="Publishing">
            <div className="px-4 py-3">
              {capability.isLoading ? (
                <Skeleton className="h-4 w-48" />
              ) : (
                <p className="text-muted text-[12.5px] text-pretty">
                  {capability.data?.message ?? 'Publishing capability is unknown.'}
                </p>
              )}
            </div>
          </Panel>

          {!healthy && domain.state !== 'ACTIVE' && (
            <div className="border-line bg-panel2 rounded-[9px] border px-4 py-3">
              <p className="text-muted text-[12px] text-pretty">
                <strong className="text-text font-medium">Activation is gated.</strong> §18 refuses
                to activate a domain whose authentication has not verified — the API returns a 409
                with the reason, so this is not something the screen can wave through.
              </p>
            </div>
          )}
        </div>
      </div>
    </Screen>
  )
}

/** A check that is not healthy: what is wrong, and the record that fixes it. */
function CheckBlock({ check }: { check: AuthCheck }) {
  const tone = HEALTH_TONE[check.state]

  return (
    <div className="border-line2 border-b px-4 py-4 last:border-b-0">
      <div className="flex items-start gap-2.5">
        <span className="mt-[5px]">
          <Dot tone={tone} label={HEALTH_WORD[check.state]} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-text text-[13px] font-medium">{check.name}</h3>
            <Chip tone={tone}>{HEALTH_WORD[check.state]}</Chip>
          </div>
          <p className="text-muted mt-1 text-[12.5px] text-pretty">{check.summary}</p>

          {check.notes?.map((note) => (
            <p key={note} className="text-faint mt-1.5 text-[11.5px] text-pretty">
              {note}
            </p>
          ))}

          {check.found && <RecordLine label="Found" value={check.found} />}
          {check.fix && <RecordLine label="Publish" value={check.fix.value} host={check.fix.host} />}
        </div>
      </div>
    </div>
  )
}

function PassingBlock({ check }: { check: AuthCheck }) {
  return (
    <div className="border-line2 flex items-center gap-2.5 border-b px-4 py-2.5 last:border-b-0">
      <Dot tone="ok" label="verified" />
      <span className="text-text w-14 shrink-0 text-[12.5px]">{check.name}</span>
      <span className="text-muted min-w-0 flex-1 truncate text-[12px]">{check.summary}</span>
    </div>
  )
}

/** A DNS record, in a shape somebody can copy into a provider's dashboard without transcribing. */
function RecordLine({ label, value, host }: { label: string; value: string; host?: string }) {
  const [copied, setCopied] = useState(false)

  return (
    <div className="mt-2.5">
      <div className="text-faint mb-1 flex items-center gap-2 text-[11px]">
        <span>{label}</span>
        {host && <span className="font-mono">{host}</span>}
      </div>
      <div className="bg-panel2 border-line flex items-start gap-2 rounded-[7px] border p-2.5">
        <code className="text-text min-w-0 flex-1 font-mono text-[11.5px] leading-relaxed break-all">
          {value}
        </code>
        <IconButton
          icon={copied ? 'check' : 'copy'}
          label={copied ? 'Copied' : 'Copy record'}
          size="sm"
          variant="quiet"
          onClick={async () => {
            await navigator.clipboard.writeText(value)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          }}
        />
      </div>
    </div>
  )
}

// ── registering ───────────────────────────────────────────────────────────────

/**
 * Adding a domain runs all four checks against real public DNS at registration time, so the
 * operator learns immediately what is already in place rather than after a separate step.
 */
function AddDomainDialog({
  open,
  onClose,
  onOpen,
}: {
  open: boolean
  onClose: () => void
  onOpen: (id: string) => void
}) {
  const queryClient = useQueryClient()
  const [fqdn, setFqdn] = useState('')
  const [purpose, setPurpose] = useState<DomainPurpose>('ACQUISITION_OUTREACH')
  const [error, setError] = useState<string | null>(null)

  const register = useMutation({
    mutationFn: () => api.post<{ domain: Domain }>('/domains', { fqdn: fqdn.trim(), purpose }),
    onSuccess: async (result) => {
      setFqdn('')
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['domains'] })
      onClose()
      onOpen(result.domain.id)
    },
    onError: (e: Error) => setError(e.message),
  })

  const selected = PURPOSES.find((p) => p.value === purpose)

  return (
    <Dialog
      open={open}
      title="Add a domain"
      description="Its authentication is checked against public DNS as soon as it is registered."
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!fqdn.trim() || register.isPending}
            onClick={() => register.mutate()}
          >
            {register.isPending ? 'Checking DNS…' : 'Add domain'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="Domain"
          hint="The hostname mail is sent from — not the domain your website is on."
          error={error}
        >
          {(props) => (
            <Input
              {...props}
              value={fqdn}
              placeholder="send.example.org"
              onChange={(e) => setFqdn(e.target.value)}
              className="font-mono"
            />
          )}
        </Field>

        <Field label="Purpose" hint={selected?.hint}>
          {(props) => (
            <Select
              {...props}
              value={purpose}
              onChange={(e) => setPurpose(e.target.value as DomainPurpose)}
            >
              {PURPOSES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
    </Dialog>
  )
}
